import type { ReactNode } from 'react'
import { clsx } from 'clsx'

export interface AlertProps {
  children: ReactNode
  variant?: 'info' | 'success' | 'warning' | 'danger'
  title?: string
  icon?: ReactNode
  onClose?: () => void
  className?: string
}

export function Alert({ children, variant = 'info', title, icon, onClose, className }: AlertProps) {
  const variants = {
    info: {
      container: 'bg-primary-50 border-primary-200',
      icon: 'text-primary-600',
      title: 'text-primary-800',
      text: 'text-primary-700',
    },
    success: {
      container: 'bg-secondary-50 border-secondary-200',
      icon: 'text-secondary-600',
      title: 'text-secondary-800',
      text: 'text-secondary-700',
    },
    warning: {
      container: 'bg-warning-50 border-warning-200',
      icon: 'text-warning-600',
      title: 'text-warning-800',
      text: 'text-warning-700',
    },
    danger: {
      container: 'bg-danger-50 border-danger-200',
      icon: 'text-danger-600',
      title: 'text-danger-800',
      text: 'text-danger-700',
    },
  }

  const styles = variants[variant]

  return (
    <div className={clsx('rounded-lg border p-4', styles.container, className)} role="alert">
      <div className="flex">
        {icon && <div className={clsx('flex-shrink-0 mr-3', styles.icon)}>{icon}</div>}
        <div className="flex-1">
          {title && <h3 className={clsx('text-sm font-medium mb-1', styles.title)}>{title}</h3>}
          <div className={clsx('text-sm', styles.text)}>{children}</div>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className={clsx(
              'flex-shrink-0 ml-3 p-1 rounded-md hover:bg-black/5 transition-colors',
              styles.icon
            )}
          >
            <span className="sr-only">Cerrar</span>
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  )
}
